<template>
  <div class="traffic-light-legend" :class="{ 'expanded': isExpanded }" @mouseenter="isExpanded = true" @mouseleave="isExpanded = false">
    <!-- Collapsed Tab (Visible always on the edge) -->
    <div class="pull-tab" @click="isExpanded = !isExpanded">
      <div class="tab-light green"></div>
      <div class="tab-light yellow"></div>
      <div class="tab-light orange"></div>
      <div class="tab-light red"></div>
    </div>

    <!-- Expanded Content -->
    <div class="legend-content">
      <div class="legend-header d-flex align-items-center justify-content-between mb-3">
        <h6 class="text-white mb-0">{{ t.title }}</h6>
        <!-- Language Toggle Button -->
        <button class="btn btn-xs btn-outline-light language-toggle" @click.stop="toggleLanguage">
          {{ language === 'en' ? 'ES' : 'EN' }}
        </button>
      </div>
      <div class="light-container">
        <!-- Green -->
        <div class="light-item" @click="activeColor = activeColor === 'green' ? null : 'green'">
          <div class="d-flex align-items-center gap-2 mb-1 w-100">
            <div class="light green"></div>
            <span class="light-label">{{ t.greenLabel }}</span>
          </div>
          <transition name="fade">
            <div v-if="activeColor === 'green'" class="light-description green-border">
              {{ t.greenDesc }}
            </div>
          </transition>
        </div>

        <!-- Yellow -->
        <div class="light-item" @click="activeColor = activeColor === 'yellow' ? null : 'yellow'">
          <div class="d-flex align-items-center gap-2 mb-1 w-100">
            <div class="light yellow"></div>
            <span class="light-label">{{ t.yellowLabel }}</span>
          </div>
          <transition name="fade">
            <div v-if="activeColor === 'yellow'" class="light-description yellow-border">
              {{ t.yellowDesc }}
            </div>
          </transition>
        </div>

        <!-- Orange -->
        <div class="light-item" @click="activeColor = activeColor === 'orange' ? null : 'orange'">
          <div class="d-flex align-items-center gap-2 mb-1 w-100">
            <div class="light orange"></div>
            <span class="light-label">{{ t.orangeLabel }}</span>
          </div>
          <transition name="fade">
            <div v-if="activeColor === 'orange'" class="light-description orange-border">
              {{ t.orangeDesc }}
            </div>
          </transition>
        </div>

        <!-- Red -->
        <div class="light-item" @click="activeColor = activeColor === 'red' ? null : 'red'">
          <div class="d-flex align-items-center gap-2 mb-1 w-100">
            <div class="light red"></div>
            <span class="light-label">{{ t.redLabel }}</span>
          </div>
          <transition name="fade">
            <div v-if="activeColor === 'red'" class="light-description red-border">
              {{ t.redDesc }}
            </div>
          </transition>
        </div>
      </div>
      <div class="mt-3 text-muted" style="font-size: 10px;">
        {{ t.help }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrafficLightLegend',
  data() {
    return {
      isExpanded: false,
      activeColor: null,
      language: localStorage.getItem('language') === 'sp' ? 'sp' : 'en'
    };
  },
  computed: {
    t() {
      const translations = {
        en: {
          title: 'Visit Status',
          greenLabel: 'Recently Visited',
          greenDesc: 'Visited within the last 90 days.',
          yellowLabel: 'Follow-up Needed',
          yellowDesc: 'Visited between 91 and 180 days ago.',
          orangeLabel: 'Pending',
          orangeDesc: 'Marked manually by the user, but only if the visit is <= 180 days ago.',
          redLabel: 'Overdue / Never',
          redDesc: 'Never visited, or last visit was more than 180 days ago.',
          help: 'Click a color for details'
        },
        sp: {
          title: 'Estado de Visita',
          greenLabel: 'Recién Visitado',
          greenDesc: 'Visitado en los últimos 90 días.',
          yellowLabel: 'Seguimiento',
          yellowDesc: 'Visitado hace entre 91 y 180 días.',
          orangeLabel: 'Pendiente',
          orangeDesc: 'Marcado manualmente por el usuario (siempre que la visita sea <= 180 días).',
          redLabel: 'Atrasado / Nunca',
          redDesc: 'Nunca visitado, o la última visita fue hace más de 180 días.',
          help: 'Click en un color para ver detalles'
        }
      };
      return translations[this.language];
    }
  },
  methods: {
    toggleLanguage() {
      this.language = this.language === 'en' ? 'sp' : 'en';
    }
  }
};
</script>

<style scoped>
.btn-xs {
  padding: 1px 5px;
  font-size: 0.6rem;
  line-height: 1.2;
  border-radius: 3px;
}

.language-toggle {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.language-toggle:hover {
  opacity: 1;
}

.traffic-light-legend {
  position: fixed;
  right: -200px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  transition: right 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.traffic-light-legend.expanded {
  right: 0;
}

.legend-content {
  background: #212529;
  padding: 18px;
  border-radius: 12px 0 0 12px;
  box-shadow: -10px 0 20px rgba(0, 0, 0, 0.5);
  width: 200px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-right: none;
}

.pull-tab {
  background: #212529;
  padding: 15px 8px;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-right: none;
  box-shadow: -5px 0 10px rgba(0, 0, 0, 0.3);
}

.light, .tab-light {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tab-light {
  width: 10px;
  height: 10px;
}

.green { background-color: #0ab39c; box-shadow: 0 0 8px rgba(10, 179, 156, 0.6); }
.yellow { background-color: #fcf458; box-shadow: 0 0 8px rgba(252, 244, 88, 0.6); }
.orange { background-color: #f7b84b; box-shadow: 0 0 8px rgba(247, 184, 75, 0.6); }
.red { background-color: #f06548; box-shadow: 0 0 8px rgba(240, 101, 72, 0.6); }

.light-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.light-item {
  display: flex;
  flex-wrap: wrap;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.2s ease;
  border: 1px solid transparent;
}

.light-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.light-label {
  color: #ced4da;
  font-size: 13px;
  font-weight: 500;
}

.light-description {
  width: 100%;
  font-size: 11px;
  color: #fff;
  background: rgba(0, 0, 0, 0.4);
  padding: 8px;
  border-radius: 4px;
  margin-top: 5px;
  line-height: 1.4;
}

.green-border { border-left: 3px solid #0ab39c; }
.yellow-border { border-left: 3px solid #fcf458; }
.orange-border { border-left: 3px solid #f7b84b; }
.red-border { border-left: 3px solid #f06548; }

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
